import { useState, useEffect, useRef } from 'react'

import { TURNSTILE_SCRIPT_URL, CLOUDFLARE_TURNSTILE_SITE_KEY } from './constant'
import { TurnstileProps, TurnstileOptions } from './types'

export default function CloudflareTurnstileWidget({
  siteKey,
  onVerify,
  theme,
  size,
  action,
  cData,
  responseField,
  responseFieldName,
  className,
}: TurnstileProps): JSX.Element {
  const widgetRef = useRef<HTMLDivElement>(null)
  const [widgetId, setWidgetId] = useState<string | null>(null)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  const onVerifyRef = useRef(onVerify)
  useEffect(() => {
    onVerifyRef.current = onVerify
  }, [onVerify])

  useEffect(() => {
    if (window.turnstile) {
      setIsScriptLoaded(true)
      return
    }

    const thisCallback = () => {
      setIsScriptLoaded(true)
    }

    if (window.turnstileCallbacks) {
      window.turnstileCallbacks.push(thisCallback)
    } else {
      window.turnstileCallbacks = [thisCallback]

      window.onloadTurnstileCallback = () => {
        if (window.turnstileCallbacks) {
          window.turnstileCallbacks.forEach((cb) => cb())
        }
        delete window.onloadTurnstileCallback
        delete window.turnstileCallbacks
      }

      const script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT_URL
      script.async = true
      script.defer = true
      script.onerror = () => {
        console.error('Failed to load Cloudflare Turnstile script.')
        delete window.onloadTurnstileCallback
        delete window.turnstileCallbacks
      }
      document.head.appendChild(script)
    }

    return () => {
      if (window.turnstileCallbacks) {
        window.turnstileCallbacks = window.turnstileCallbacks.filter(
          (cb) => cb !== thisCallback,
        )
      }
    }
  }, [])

  useEffect(() => {
    if (!isScriptLoaded || !widgetRef.current) {
      return
    }

    const options: TurnstileOptions = {
      sitekey: siteKey || CLOUDFLARE_TURNSTILE_SITE_KEY || '',
      callback: (token: string) => onVerifyRef.current(token),
      theme,
      size,
      action,
      cData,
      'response-field': responseField,
      'response-field-name': responseFieldName,
    }

    if (!widgetId) {
      const newWidgetId = window.turnstile.render(widgetRef.current, options)
      setWidgetId(newWidgetId)
    }

    return () => {
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId)
        setWidgetId(null)
      }
    }
  }, [
    isScriptLoaded,
    widgetId,
    siteKey,
    theme,
    size,
    action,
    cData,
    responseField,
    responseFieldName,
  ])

  return <div ref={widgetRef} className={className} />
}
